/* eslint-disable eslint-comments/no-restricted-disable -- Need to disable for now */

/* eslint-disable sonarjs/cognitive-complexity -- Not much to refactor at the moment */
import type {
  ComponentPropsWithRef,
  FocusEvent,
  KeyboardEvent,
  MouseEvent,
} from "react";
import { forwardRef, useCallback, useEffect, useRef, useState } from "react";
import mergeRefs from "merge-refs";
import { useDeviceContext } from "@/react/components/shared/BuilderUI/DeviceProvider";
import { classNames } from "@/react/helpers/twMergeWithCN";
import {
  useBuilderStore,
  useSelectedBlock,
} from "@circle-react-shared/BuilderUI";
import type {
  BlockRendererMapping,
  BlockValues,
} from "@circle-react-shared/BuilderUI/BuilderUI.types";
import type { BaseBlock } from "@circle-react-shared/BuilderUI/DataModel.types";
import {
  isChildrenProp,
  isRootBlock,
} from "@circle-react-shared/BuilderUI/Helpers/typePredicates";
import { isNonNullableUnknown } from "../Helpers/typePredicates";
import { PreviewBlockHover } from "./PreviewBlockHover";
import { PreviewBlockList } from "./PreviewBlockList";
import { flattenResponsiveStylesDeep } from "./flattenResponsiveStylesDeep";
import { getAcceptsRejectsConfig } from "./getAcceptsRejectsConfig";

export interface PreviewProps extends ComponentPropsWithRef<"div"> {
  block: BaseBlock;
  components: BlockRendererMapping;
  path: string;
  shouldShowBlockHover?: boolean;
}

export const Preview = forwardRef<HTMLElement, PreviewProps>(
  (
    {
      block,
      components,
      path,
      className,
      shouldShowBlockHover = true,
      ...externalProps
    },
    forwardedRef,
  ) => {
    const scrollRef = useRef<HTMLElement>(null);
    const { setSelectedBlockPath, selectedBlock } = useSelectedBlock();

    const [isHovering, setIsHovering] = useState(false);
    const startInlineEditing = useBuilderStore(
      state => state.startInlineEditing,
    );
    const commitInlineEdit = useBuilderStore(state => state.commitInlineEdit);
    const getBlockMeta = useBuilderStore(state => state.getBlockMeta);
    const closeSidebarView = useBuilderStore(state => state.closeSidebarView);
    const shouldAllowInlineEditing = useBuilderStore(
      state => state.shouldAllowInlineEditing,
    );

    const isBlockWithTextProp = block.props.text != null;

    const selectedBlockId = selectedBlock?.id;
    const device = useDeviceContext();
    const currentBlockId = block.id;
    const externalOnClick = externalProps.onClick;
    const isCurrentBlockSelected = selectedBlockId === currentBlockId;
    const isInlineEditing = useBuilderStore(state => state.isInlineEditing);
    const isCurrentlyInlineEditing = isInlineEditing(path);

    const handleClick = useCallback(
      (event: MouseEvent<HTMLDivElement>) => {
        externalOnClick?.(event);
        event.stopPropagation();
        setSelectedBlockPath(path);
        closeSidebarView();
      },
      [externalOnClick, path, setSelectedBlockPath, closeSidebarView],
    );

    const onMouseOver = useCallback((event: MouseEvent<HTMLDivElement>) => {
      event.stopPropagation();
      setIsHovering(true);
    }, []);

    const onMouseOut = useCallback((event: MouseEvent<HTMLDivElement>) => {
      event.stopPropagation();
      setIsHovering(false);
    }, []);

    const handleDoubleClick = useCallback(
      (event: MouseEvent<HTMLDivElement>) => {
        if (isBlockWithTextProp && shouldAllowInlineEditing) {
          event.stopPropagation();
          startInlineEditing(path);
        }
      },
      [startInlineEditing, path, isBlockWithTextProp, shouldAllowInlineEditing],
    );

    const handleKeyDown = useCallback(
      (event: KeyboardEvent<HTMLDivElement>) => {
        if (
          isCurrentlyInlineEditing &&
          isBlockWithTextProp &&
          (event.key === "Enter" || event.key === "Escape")
        ) {
          event.preventDefault();
          const newText = (event.target as HTMLDivElement).textContent || "";
          commitInlineEdit(newText);
        }
      },
      [isCurrentlyInlineEditing, isBlockWithTextProp, commitInlineEdit],
    );

    const handleBlur = useCallback(
      (event: FocusEvent<HTMLDivElement>) => {
        if (isCurrentlyInlineEditing && isBlockWithTextProp) {
          const newText = event.target.textContent || "";
          commitInlineEdit(newText);
        }
      },
      [isCurrentlyInlineEditing, isBlockWithTextProp, commitInlineEdit],
    );

    // Focus and select text when entering inline editing mode
    useEffect(() => {
      if (
        isCurrentlyInlineEditing &&
        isBlockWithTextProp &&
        scrollRef.current
      ) {
        scrollRef.current.focus();

        const selection = window.getSelection();
        const range = document.createRange();
        range.selectNodeContents(scrollRef.current);
        selection?.removeAllRanges();
        selection?.addRange(range);
      }
    }, [isCurrentlyInlineEditing, isBlockWithTextProp]);

    const PreviewComponent = components[block.type];
    const blockMeta = getBlockMeta(block.type);
    if (!PreviewComponent || !blockMeta) return null;

    const entries = Object.entries(block.props);
    const shouldNotAddEvents = isRootBlock(block);
    const ref = mergeRefs(scrollRef, forwardedRef);

    const blockProps = entries.reduce<BlockValues>((props, [name, prop]) => {
      if (isChildrenProp(prop)) {
        const { accepts, rejects } = getAcceptsRejectsConfig(blockMeta, name);

        props[name] = (
          <PreviewBlockList
            accepts={accepts}
            rejects={rejects}
            blocks={prop.value}
            components={components}
            path={`${path}.props.${name}.value`}
            shouldShowBlockHover={shouldShowBlockHover}
          />
        );
      } else if (isNonNullableUnknown(prop.value)) {
        if (name === "styles") {
          props[name] = flattenResponsiveStylesDeep(prop.value, device);
        } else {
          props[name] = prop.value;
        }
      }
      return props;
    }, {});

    if (shouldNotAddEvents) {
      return (
        <PreviewComponent
          data-preview-mode
          data-block
          data-block-type={block.type}
          {...blockProps}
        />
      );
    }

    const shouldShowHover =
      shouldShowBlockHover && isHovering && !isCurrentBlockSelected;

    return (
      <div
        data-preview-component-wrapper
        className="wb-relative wb-select-none wb-border-box"
      >
        {shouldShowHover && (
          <PreviewBlockHover
            blockMeta={blockMeta}
            isFirstBlock={path === "blocks.props.children.value.0"}
          />
        )}
        <PreviewComponent
          {...blockProps}
          {...externalProps}
          key={currentBlockId}
          id={currentBlockId}
          ref={ref}
          data-preview-mode
          data-preview-mode-is-inline-editing={isCurrentlyInlineEditing}
          data-block
          data-block-type={block.type}
          data-inline-editable={isBlockWithTextProp && shouldAllowInlineEditing}
          onClick={handleClick}
          onMouseOver={onMouseOver}
          onMouseOut={onMouseOut}
          onDoubleClick={handleDoubleClick}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          contentEditable={blockMeta && isCurrentlyInlineEditing}
          suppressContentEditableWarning
          className={classNames(className, {
            "preview--isInlineEditing wb-outline-none":
              isCurrentlyInlineEditing,
          })}
        />
      </div>
    );
  },
);
Preview.displayName = "Preview";
